import React, { useContext, useEffect, useState } from 'react';
import { EllipsisVerticalIcon, EyeIcon, PencilIcon, PrinterIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from '../../../components/Dropdown';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import Loading from '../../../components/Loading';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../../context/userContext';
import { WarehouseProduct } from '../../../models/WarehouseProduct';
import { useWarehouseProductItem } from '../../../hooks/useWarehouseProductItem';
import { WarehouseProductItem } from '../../../models/WarehouseProductItem';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import { Text } from '../../../components/Text';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { useWarehouseProduct } from '../../../hooks/useWarehouseProduct';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';

const COLUMNS = [
    { key: 'ccode', label: 'CCode' },
    { key: 'sku', label: 'SKU' },
    { key: 'secondItemNo', label: 'Second Item No' },
    { key: 'description', label: 'Description' },
    { key: 'minQuantity', label: 'Minimum Quantity' },
    { key: 'currentQuantity', label: 'Current Quantity' },
    { key: 'created', label: 'Created Date' },
];
const SORT_OPTIONS = [
    { label: 'Item No (A-Z)', value: 'az' },
    { label: 'Item No (Z-A)', value: 'za' },
];

const ReportStockBalancePage: React.FC = () => {
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');
    const { currentUser } = useContext(UserContext); // Get current user info
    const {
        loading,
        error,
        products,
        fetchAllFilteredProduct
    } = useWarehouseProduct();
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [currentPage, setCurrentPage] = useState<number>(1); // State to track the current page
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null); // ID of the product to be deleted
    const navigate = useNavigate();
    const [filterStartDate, setFilterStartDate] = useState<string>('');
    const [filterEndDate, setFilterEndDate] = useState<string>('');
    const [filterWarehouseId, setFilterWarehouseId] = useState<string>('');
    const [filterYear, setFilterYear] = useState<string>('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('az');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const { warehouses } = useWarehouses();

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(products) ? products : [])
                .map((p) => new Date(p.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    useEffect(() => {
        document.title = "Stock Balance Report";
        fetchAllFilteredProduct();
    }, []);

    // Filter products based on the search query
    const filteredProduct = Array.isArray(products)
        ? products?.filter((product) => {
            const matchesSearch = product.item_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                product.second_item_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                product.description?.toLowerCase().includes(searchQuery.toLowerCase())

            const matchesStartDate = !filterStartDate || new Date(product.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(product.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(product.created_time).getFullYear().toString() === filterYear;
            const matchesWarehouse = !filterWarehouseId || Number(product.warehouse_id) === Number(filterWarehouseId);

            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
        }) : [];

    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Function to generate PDF
    const generatePDF = () => {
        const today = formatDate(new Date());

        if (!filteredProduct) {
            showErrorToast("PDF generation failed. Product item is not available.");
            return; // Exit the function early if productItem is undefined
        }

        const doc = new jsPDF();
        doc.setFontSize(12);
        doc.text(`Stock Balance Report | ${today}`, 14, 10);
        doc.autoTable({
            head: [['S/N', 'CCode', 'SKU', 'Item No', 'Second Item No', 'Description', 'Minimum Quantity', 'Current Quantity']],
            body: filteredProduct.map((product, index) => [
                index + 1,
                product.sku || "N/A",
                product.item_no || "N/A",
                product.second_item_no || "N/A",
                product.description || "N/A",
                product.minimum_quantity || 0,
                product.current_quantity || 0
            ])
        });

        doc.save(`stock_balance_report_${today}.pdf`);
    };

    // Function to generate CSV
    const generateCSV = () => {
        if (!filteredProduct) {
            showErrorToast("CSV generation failed. Product item is not available.");
            return; // Exit the function early if productItem is undefined
        }

        const csvData = [
            ['S/N', 'CCode', 'SKU', 'Item No', 'Second Item No', 'Description', 'Minimum Quantity', 'Current Quantity'],
            ...filteredProduct.map((product, index) => [
                index + 1,
                product.sku || "N/A",
                product.item_no || "N/A",
                product.second_item_no || "N/A",
                product.description || "N/A",
                product.minimum_quantity || 0,
                product.current_quantity || 0
            ])
        ];

        const csvContent = 'data:text/csv;charset=utf-8,' + csvData.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        const today = formatDate(new Date());
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `stock_balance_report_${today}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Function to generate Excel
    const generateExcel = async () => {
        if (!filteredProduct) {
            showErrorToast("Excel generation failed. Product item is not available.");
            return;
        }

        const data = filteredProduct.map((product, index) => ({
            SNo: index + 1,
            SKU: product.sku || "N/A",
            ItemNo: product.item_no || "N/A",
            SecondItemNo: product.second_item_no || "N/A",
            Description: product.description || "N/A",
            MinimumQuantity: product.minimum_quantity || 0,
            CurrentQuantity: product.current_quantity || 0
        }));

        if (data.length === 0) {
            showErrorToast("Excel generation failed. No data available.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Stock Balance");
        sheet.columns = Object.keys(data[0]).map((key) => ({ header: key, key, width: 18 }));
        sheet.addRows(data);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `stock_balance_report_${formatDate(new Date())}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Stock Balance</Heading>
                </div>
                <Dropdown>
                    <DropdownButton aria-label="More options">
                        Print Report
                    </DropdownButton>
                    <DropdownMenu anchor="bottom end">
                        <DropdownItem onClick={generatePDF}>PDF</DropdownItem>
                        <DropdownItem onClick={generateCSV}>CSV</DropdownItem>
                        <DropdownItem onClick={generateExcel}>Excel</DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search stock balance…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'ccode') && <TableHeader>CCode</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'sku') && <TableHeader>SKU</TableHeader>}
                                    <TableHeader>Item No</TableHeader>
                                    {isColumnVisible(columnVisibility, 'secondItemNo') && <TableHeader>Second Item No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'description') && <TableHeader>Description</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'minQuantity') && <TableHeader>Minimum Quantity</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'currentQuantity') && <TableHeader>Current Quantity</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created Date</TableHeader>}
                                    <TableHeader>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredProduct.length > 0) ? [...filteredProduct].sort((a, b) => sortOrder === 'az' ? (a.item_no || '').localeCompare(b.item_no || '') : (b.item_no || '').localeCompare(a.item_no || '')).map((product, index) => (
                                    <TableRow key={index} title={`${index}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'ccode') && <TableCell>{product.category_code || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'sku') && <TableCell>{product.sku || '-'}</TableCell>}
                                        <TableCell className="text-zinc-500">{product.item_no || '-'}</TableCell>
                                        {isColumnVisible(columnVisibility, 'secondItemNo') && <TableCell className="text-zinc-500">{product.second_item_no || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'description') && <TableCell className="text-zinc-500">{product.description || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'minQuantity') && <TableCell className="text-zinc-400">{product.minimum_quantity || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'currentQuantity') && <TableCell className="text-zinc-400">{product.current_quantity || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-400">{dateFormat(product.created_time, "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4">
                                            <a className="text-zinc-600 hover:text-zinc-900" onClick={() => navigate(`/gp/reports/stockbalance/view/${product.id}`)}>
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No stock balance found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>
        </>
    );
};

export default ReportStockBalancePage;
